import { useMemo } from "react";
import { useGetRiskAnalysisBySession } from "@/hooks/api/query/useGetRiskAnalysisBySession";
import { useGetMaturityEvaluation } from "@/hooks/api/query/useGetMaturityEvaluation";
import {
  getAnalizaRiscStatus,
  getMaturityStatus,
  getNotificationPdfChecks,
  getNotificationPdfStatusFromChecks,
  isRiskAnalysisUnlocked,
} from "@/utils/guideProgressCalculator";
import { mapCyFunToRiskLevel } from "@/utils/risk-calculator";
import {
  mapDashboardToPortfolioStatus,
  mapMaturityToPortfolioStatus,
  type PortfolioCardData,
  type PortfolioStep,
  type PortfolioStepStatus,
} from "@/utils/portfolioCardData";

export function useCompanyCardStatuses(
  card: PortfolioCardData
): PortfolioCardData {
  const sessionId = card.id;
  const entityData = card.session.entity_data;

  const notificareStatus = mapDashboardToPortfolioStatus(
    getNotificationPdfStatusFromChecks(getNotificationPdfChecks(entityData))
  );
  const isNotificareCompleted = isRiskAnalysisUnlocked(entityData);

  const { data: riskData, isLoading: isLoadingRisk } =
    useGetRiskAnalysisBySession(sessionId, { retry: false });

  const isRiscCompleted = getAnalizaRiscStatus(riskData) === "completed";

  const { data: maturityEval, isLoading: isLoadingMaturity } =
    useGetMaturityEvaluation(isRiscCompleted ? sessionId : undefined);

  return useMemo(() => {
    const riscStatus: PortfolioStepStatus = (() => {
      if (isLoadingRisk) return "loading";
      if (!isNotificareCompleted) return "Blocat";
      return mapDashboardToPortfolioStatus(getAnalizaRiscStatus(riskData));
    })();

    const maturityStatus: PortfolioStepStatus = (() => {
      if (isLoadingRisk) return "loading";
      if (!isRiscCompleted) return "Blocat";
      if (isLoadingMaturity) return "loading";
      return mapMaturityToPortfolioStatus(
        getMaturityStatus(maturityEval, isRiscCompleted)
      );
    })();

    const enrichedSteps: PortfolioStep[] = card.steps.map((step) => {
      switch (step.key) {
        case "notificare":
          return { ...step, status: notificareStatus };
        case "analiza-risc":
          return { ...step, status: riscStatus };
        case "maturitate":
          return { ...step, status: maturityStatus };
        default:
          return step;
      }
    });

    return {
      ...card,
      steps: enrichedSteps,
      riskLevel: mapCyFunToRiskLevel(riskData?.global_cyfun_level),
      maturityScore: maturityEval?.overall_score ?? null,
      maturityTarget: maturityEval?.target_score ?? null,
    };
  }, [
    card,
    notificareStatus,
    riskData,
    maturityEval,
    isLoadingRisk,
    isLoadingMaturity,
    isRiscCompleted,
    isNotificareCompleted,
  ]);
}
